import React from "react";
import { Box, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, TextField, Typography } from "@mui/material";
import { DataGrid, GridActionsCellItem, GridColDef } from "@mui/x-data-grid";
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { FieldType, InterfaceProperty, IfacePropertySchema } from "../../types/Interface";
import { WorkshopProductProperty } from "../../types/WorkshopProductProperty";

export const PropertyInput = ({ interfaceProperty, workshopProductProperty, disabled, workshopProductPropertySubmitHandler }:
    {
        interfaceProperty: InterfaceProperty, workshopProductProperty: WorkshopProductProperty,
        disabled?: boolean,
        workshopProductPropertySubmitHandler: (wpp: WorkshopProductProperty) => void
    }): React.ReactElement => {
    let inner: React.ReactElement = <></>;

    // Safely parse fieldSchema (avoid throwing on invalid JSON)
    let propertySchema: IfacePropertySchema | undefined;
    if (interfaceProperty.fieldSchema) {
        try {
            propertySchema = JSON.parse(interfaceProperty.fieldSchema) as IfacePropertySchema;
        } catch (e) {
            console.warn('Invalid JSON in interfaceProperty.fieldSchema for id', interfaceProperty.id, e);
            propertySchema = undefined;
        }
    } else {
        propertySchema = undefined;
    }

    const [inputContents, setInputContents] = React.useState<string>('');

    React.useEffect(() => {
        setInputContents(workshopProductProperty?.value ?? '')
    }, [workshopProductProperty]);

    const handlepropertySubmit = (e: React.FocusEvent<HTMLInputElement>) => {
        workshopProductPropertySubmitHandler({ ...workshopProductProperty, interfacePropertyId: interfaceProperty.id, value: e.target.value });
    }

    switch (interfaceProperty.fieldType) {
        case FieldType.CheckBox: 
            inner = <PropertyInputRadioGroup interfaceProperty={interfaceProperty} workshopProductProperty={workshopProductProperty}
                propertySchema={propertySchema} workshopProductPropertySubmitHandler={workshopProductPropertySubmitHandler} />
            break;
        case FieldType.MultiLineBox:
            console.log('MultiLineTextBox: ', JSON.stringify(propertySchema));
            inner = <TextField multiline
                label={<Typography color={'black'} variant='body2'>
                    {interfaceProperty.name}
                </Typography>}
                slotProps={{ inputLabel: { shrink: true }, }}
                maxRows={propertySchema?.maxRows ?? 0} minRows={propertySchema?.minRows ?? 0} value={inputContents}
                onChange={(e) => setInputContents(e.target.value)}
                onBlur={handlepropertySubmit} />
            break;
        /*case FieldType.OptionList:
            break;*/
        case FieldType.Table:
            inner = propertySchema ? <EditableTable tableSchema={propertySchema} disabled={disabled} workshopProductProperty={workshopProductProperty}
                interfaceProperty={interfaceProperty} workshopProductPropertySubmitHandler={workshopProductPropertySubmitHandler} />
            : <></>
            break;
        case FieldType.TextBox: inner = <TextField 
            label={<Typography color={'black'} variant='body2'>
                {interfaceProperty.name}
            </Typography>}
            defaultValue={workshopProductProperty?.value ?? ''} slotProps={{ inputLabel: { shrink: true } }}></TextField>
            break;
    }

    return <FormControl fullWidth>
        {[inner]}
    </FormControl>
}

export const PropertyInputRadioGroup = ({ interfaceProperty, workshopProductProperty, propertySchema, workshopProductPropertySubmitHandler }:
    {
        interfaceProperty: InterfaceProperty, workshopProductProperty: WorkshopProductProperty,
        propertySchema?: IfacePropertySchema,
        workshopProductPropertySubmitHandler: (wpp: WorkshopProductProperty) => void
    }): React.ReactElement => {
    const [value, setValue] = React.useState('');

    React.useEffect(() => {
        setValue(workshopProductProperty?.value)
    }, [workshopProductProperty]);

    const handlepropertyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue(e.target.value);
        workshopProductPropertySubmitHandler({ ...workshopProductProperty, interfacePropertyId: interfaceProperty.id, value: e.target.value })
    }

    return <FormControl fullWidth>
        <FormLabel id={"prooertyAsRadioGroup" + interfaceProperty.id}
            sx={{ color: 'black' }}
            color='info'>
            {interfaceProperty.name}
        </FormLabel>
        <RadioGroup value={value} aria-labelledby={"prooertyAsRadioGroup" + interfaceProperty.id}
            onChange={handlepropertyChange} name="prooertyAsTrueFalse">
            <FormControlLabel value={'-1'} label={'Yes'} control={<Radio />} />
            <FormControlLabel value={'0'} label={'No'} control={<Radio />} />
        </RadioGroup>
    </FormControl>
}

const newEmptyRow = (columnDefinitons: GridColDef[], id: number) => {
    const rowObject: { [key: string]: any } = { id: id };

    columnDefinitons.forEach((columnDefiniton, index) => {
        rowObject[columnDefiniton.field] = '';
    });

    return rowObject;
}

const removeRow = (rows: Array<{ [key: string]: any }>, id: number) => rows.filter(row => row.id !== id);

const updateRow = (rows: Array<{ [key: string]: any }>, updatedRow: { [key: string]: any }) => {
    const rowIndex = rows.findIndex(row => row.id === updatedRow.id);

    console.log('updateRow updatedRow: ', updatedRow);
    console.log('updateRow rowIndex: ', rowIndex);
    console.log('updateRow: ', [...rows.slice(0, rowIndex), updatedRow, ...rows.slice(rowIndex + 1, rows.length)])

    return [...rows.slice(0, rowIndex), updatedRow, ...rows.slice(rowIndex + 1, rows.length)];
}

const parseTableRows = (columnDefinitions: GridColDef[], rows?: Array<string[]>) => {
    const rowObjects: { [key: string]: any }[] = [];

    if (!rows || !Array.isArray(rows)) return rowObjects;

    rows.map((row, index) => {
        let rowObject: { [key: string]: any } = newEmptyRow(columnDefinitions, index);

        columnDefinitions.forEach((columnDefinition, index) =>
            rowObject[columnDefinition.field] = row[index]);

        rowObjects.push(rowObject);
        return row; // map requires a return, but we don't actually need to return anything here
    })

    return rowObjects;
}

// Build a safe JSON string for the table: array-of-arrays. Use JSON.stringify to properly escape values.
const tableRows2String = (rows: Array<{ [key: string]: any }>) => {
    // For each row object, take values excluding the leading 'id' and the trailing action column ('deleteRow') if present.
    const arr = rows.map(row => {
        const rowValues = Object.values(row);
        // slice out id (first) and last column (actions) which is typically the delete column.
        const valuesOnly = rowValues.slice(1, rowValues.length - 1);
        return valuesOnly.map(v => (v === undefined || v === null) ? '' : String(v));
    });
    return JSON.stringify(arr);
}

const EditableTable = ({ interfaceProperty, tableSchema, workshopProductProperty, disabled, workshopProductPropertySubmitHandler }:
    {
        tableSchema: IfacePropertySchema, workshopProductProperty: WorkshopProductProperty, interfaceProperty: InterfaceProperty, 
        disabled?: boolean,
        workshopProductPropertySubmitHandler: (wpp: WorkshopProductProperty) => void
    }) => {
    const columnDefintions: GridColDef[] = tableSchema.columns.map((column: string) => {
        return {
            field: column,
            headerName: column,
            flex: 1,
            editable: true,
        }
    });

    console.log('json: ', workshopProductProperty?.value);

    const [tableRows, setTableRows] = React.useState<{ [key: string]: any }[]>([]);   // there is no need to initialize this, as it will be set in useEffect

    const deleteActionColumn: GridColDef = {
        field: 'deleteRow',
        type: "actions",
        renderHeader: () => <GridActionsCellItem color="inherit" icon={<AddIcon />} label={'Add row'} onClick={() => { addRow(); }} />,
        getActions: ({ id }) => [<GridActionsCellItem icon={<DeleteIcon />} label={'Cancel'} onClick={() => { deleteRow(Number(id)); }}
        color='inherit' />]
    }
    if(!disabled) columnDefintions.push(deleteActionColumn);

    // Safely parse stored JSON for the table. If the stored value is invalid JSON, log a warning and use an empty table.
    React.useEffect(() => {
        let parsed: string[][] | undefined = undefined;
        if (workshopProductProperty?.value && workshopProductProperty.jsonError !== true) {
            try {
                parsed = JSON.parse(workshopProductProperty.value) as string[][];
            } catch (e) {
                workshopProductProperty.jsonError = true;
                console.warn('Invalid JSON in workshopProductProperty.value for interfacePropertyId', interfaceProperty.id, e, workshopProductProperty.value);
                parsed = undefined;
            }
        }
        setTableRows(parseTableRows(columnDefintions, parsed));
    }, [workshopProductProperty])       // columnDefintions changes every render, so including here creates a loop... find cleaner way to break this loop

    const addRow = () => {
        if (tableRows.length >= tableSchema.maxRows) return;
        const nextRowId = tableRows.length > 0 ? Math.max(...tableRows.map(row => row.id)) + 1 : 1; // the previous method of counting rows by length is not reliable, as rows can be deleted
        setTableRows([...tableRows, newEmptyRow(columnDefintions, nextRowId)]);
    }

    const deleteRow = (id: number) => {
//        if (tableRows.length <= tableSchema.minRows && tableSchema.isRequired) return;
        const updatedTableRows = removeRow(tableRows, id);
        workshopProductPropertySubmitHandler({ ...workshopProductProperty, interfacePropertyId: interfaceProperty.id, value: tableRows2String(updatedTableRows) });
        setTableRows(updatedTableRows);
    }

    const onRowUpdate = (newRow: { [key: string]: any }, oldRow: { [key: string]: any }) => {
        const updatedTableRows = updateRow(tableRows, newRow);
        workshopProductPropertySubmitHandler({ ...workshopProductProperty, interfacePropertyId: interfaceProperty.id, value: tableRows2String(updatedTableRows) });
        setTableRows(updatedTableRows);
        console.log('TableRows: ', tableRows2String(updatedTableRows));
        console.log('UpdatedTableRows: ', updatedTableRows);
        return newRow;
    }

    return <div style={{ display: 'flex', flexDirection: 'column' }}>
        <Box
            sx={{
                '& .required-theme--header': {
                    color: 'rgba(255, 0, 0, 1)',
                },
                '& .optional-theme--header': {
                    color: 'rgba(0, 0, 0, 1)',
                },
            }}
        >
            <Typography variant="body1" color="error" sx={{ mb: 1 }}>{workshopProductProperty?.jsonError ? "*Invalid JSON data, please re-enter the data. (note: the errored data will be removed)" : ""}</Typography>
            <DataGrid
                columns={columnDefintions}
                rows={tableRows}
                showColumnVerticalBorder
                showCellVerticalBorder
                hideFooter
                processRowUpdate={onRowUpdate}
                onProcessRowUpdateError={(error) => { console.log('ERROR: ', error); }}
                getRowClassName={(params) => params.indexRelativeToCurrentPage % 2 === 0 ? 'Mui-even' : 'Mui-odd'}
                    editMode='row' />
        </Box>
    </div>
}